import { useState, useEffect } from 'react'
import { useFormik } from 'formik'
import { residentSchema } from '../../validations/residentForm'
import { IAddResident, EditResident } from '../../interfaces/resident'
import ResidentService from '../../api/resident-service'
import ResidentTypeService from '../../api/resident-type-service'
import LandlordDataService from '../../api/landlord-service'
import FormInputRow from '../../components/Input/FormInputRow'
import DateInput from '../../components/Input/DateInput'
import FormCheckBox from '../../components/CheckBox'
import Dropdown from '../../components/Dropdown'
import DefaultButton from '../../components/Button/Button'
import { showToast } from '../../utils/toast'

interface EditResidentProps {
  handleViewSelect: (view: string, queries?: Record<string, string>) => void
  isEdit?: boolean
  resident?: EditResident & { id: string }
}

const EditResidents = ({ handleViewSelect, isEdit, resident }: EditResidentProps) => {
  const [loading, setLoading] = useState(false)
  const [residentTypes, setResidentTypes] = useState<{ value: string; label: string }[]>([])
  const [landlords, setLandlords] = useState<{ value: string; label: string }[]>([])

  const formik = useFormik({
    initialValues: {
      email: '',
      isOwner: true,
      residentType: '',
      isResident: true,
      dateOfOccupancy: '',
      address: '',
      landlord: '',
      phoneNumber: '',
      ...(resident || {}),
      capacity: 0,
    },
    enableReinitialize: true,
    validationSchema: residentSchema,
    onSubmit: () => handleSubmit(),
  })

  // Fetch resident types and landlords
  useEffect(() => {
    if (!formik.values.isResident) return

    ResidentTypeService.getResidentType({ estateId: 'your-estate-id' })
      .then(({ results }) => {
        const types = results.map((r) => ({
          value: r.id,
          label: r.title,
        }))
        setResidentTypes(types)
      })
      .catch(() => {})

    LandlordDataService.getLandlordDataList({ all: true })
      .then(({ results }) => {
        const mapped = results.map((record) => ({
          value: record.resident.id,
          label:
            `${record.resident.firstName ?? ''} ${record.resident.lastName ?? ''}`.trim() ||
            record.resident.name ||
            'Name Unavailable',
        }))
        setLandlords(mapped)
      })
      .catch(() => {})
  }, [formik.values.isResident])

  const handleSubmit = async () => {
    if (loading || !formik.dirty) return

    setLoading(true)

    const { isResident, landlord, residentType, ...values } = formik.values

    const payload: IAddResident = {
      ...values,
      isResident,
      residentType: isResident ? residentType : undefined,
      landlord: isResident && !formik.values.isOwner ? landlord : undefined,
    }

    const request = isEdit
      ? ResidentService.updateResidentType('your-estate-id', resident?.id || '', payload)
      : ResidentService.addResident(payload)

    request
      .then(() => {
        showToast({
          title: isEdit ? 'Resident updated successfully' : 'Resident added successfully',
          description: isEdit
            ? 'The resident information has been updated.'
            : 'An email has been sent. You’ll be notified of updates.',
          id: isEdit ? 'edit-resident-success' : 'create-resident-success',
          status: 'success',
        })
        handleViewSelect('view')
      })
      .catch((err) => {
        showToast({
          title: isEdit ? 'Error updating resident' : 'Error creating resident',
          description: err.message,
          id: isEdit ? 'edit-resident-error' : 'create-resident-error',
          status: 'error',
        })
      })
      .finally(() => setLoading(false))
  }

  return (
    <div className="mt-12 grid gap-y-10">
      <div className="mt-5 flex flex-col space-y-10 text-gray-400 font-medium">
        <p className="text-lg font-bold leading-7 text-black">Resident Details</p>

        <div className="space-y-2.5">
          <p className="font-medium leading-6 text-[#6C757D]">Select an Option</p>
          <div className="flex space-x-5">
            <FormCheckBox
              setChecked={(val) => formik.setFieldValue('isOwner', val)}
              checked={formik.values.isOwner}
              label="Property Owner"
              error={formik.errors.isOwner}
            />
            <FormCheckBox
              setChecked={(val) => formik.setFieldValue('isResident', val)}
              checked={formik.values.isResident}
              label="Resident"
              error={formik.errors.isResident}
            />
          </div>
        </div>

        <div className="flex w-full space-x-3.5">
          <FormInputRow
            label="Email Address"
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            placeholder="Enter your email"
            error={formik.errors.email}
            className="w-2/5 p-2.5"
          />
          <FormInputRow
            label="Address"
            name="address"
            value={formik.values.address}
            onChange={formik.handleChange}
            placeholder="Captain John Street, Close 5, Amen Estate"
            error={formik.errors.address}
            className="w-2/5 p-2.5"
          />
        </div>

        <div className="flex w-full space-x-3.5">
          <DateInput
            name="dateOfOccupancy"
            label="Date of Occupancy"
            value={formik.values.dateOfOccupancy}
            onChange={formik.handleChange}
            placeholder="19/07/2023"
            error={formik.errors.dateOfOccupancy}
            className="w-2/5 p-2.5"
          />
        </div>

        <div className="flex w-full space-x-3.5">
          {formik.values.isResident && (
            <div className="w-6/12 space-y-2.5">
              <p className="text-black font-medium text-base leading-7">Resident Type</p>
              <Dropdown
                options={residentTypes}
                value={formik.values.residentType}
                onSelect={(val) => formik.setFieldValue('residentType', val)}
                defaultLabel="Select resident type"
              />
            </div>
          )}

          {formik.values.isResident && !formik.values.isOwner && (
            <div className="w-6/12 space-y-2.5">
              <p className="text-black font-medium text-base leading-7">Landlord</p>
              <Dropdown
                options={landlords}
                value={formik.values.landlord}
                onSelect={(val) => formik.setFieldValue('landlord', val)}
                defaultLabel="Select landlord"
              />
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end mt-10 py-3 px-6">
        <DefaultButton
          text="Submit"
          onClick={handleSubmit}
          isLoading={loading}
          loadingText={isEdit ? 'Updating...' : 'Creating...'}
          disabled={loading}
          className="sm:w-54 grow sm:grow-0"
        />
      </div>
    </div>
  )
}

export default EditResidents
